import type { AddressDataset, CountryCode, CountryIsoCode, Currency } from '@src/i18n/locales'
import { locales } from '@src/i18n/locales'

export interface CountryInfo {
  countryCode: CountryCode
  countryIsoCode: CountryIsoCode
  currency: Currency
  addressDataset: AddressDataset
  regions: Array<{ name: string; code: string }>
  labels: Record<string, string>
}

const USA: CountryInfo = {
  countryCode: locales['en-US']?.countryCode,
  countryIsoCode: locales['en-US']?.countryIsoCode,
  currency: locales['en-US']?.currency,
  addressDataset: locales['en-US']?.addressDataset,
  labels: {
    region: 'State',
  },
  regions: [
    { code: 'AA', name: 'Armed Forces Americas' },
    { code: 'AE', name: 'Armed Forces Europe' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AL', name: 'Alabama' },
    { code: 'AP', name: 'Armed Forces Pacific' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DC', name: 'District Of Columbia' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'FM', name: 'Fed States of Micronesia' },
    { code: 'GA', name: 'Georgia' },
    { code: 'GU', name: 'Guam' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'IA', name: 'Iowa' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MD', name: 'Maryland' },
    { code: 'ME', name: 'Maine' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MT', name: 'Montana' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NY', name: 'New York' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'PW', name: 'Palau' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VA', name: 'Virginia' },
    { code: 'VI', name: 'Virgin Islands' },
    { code: 'VT', name: 'Vermont' },
    { code: 'WA', name: 'Washington' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WY', name: 'Wyoming' },
  ],
}

const CAN: CountryInfo = {
  countryCode: locales['en-CA']?.countryCode,
  countryIsoCode: locales['en-CA']?.countryIsoCode,
  currency: locales['en-CA']?.currency,
  addressDataset: locales['en-CA']?.addressDataset,
  labels: {
    region: 'Province',
  },
  regions: [
    { name: 'Alberta', code: 'AB' },
    { name: 'British Columbia', code: 'BC' },
    { name: 'Manitoba', code: 'MB' },
    { name: 'New Brunswick', code: 'NB' },
    { name: 'Newfoundland and Labrador', code: 'NL' },
    { name: 'Northwest Territories', code: 'NT' },
    { name: 'Nova Scotia', code: 'NS' },
    { name: 'Nunavut', code: 'NU' },
    { name: 'Ontario', code: 'ON' },
    { name: 'Prince Edward Island', code: 'PE' },
    { name: 'Quebec', code: 'QC' },
    { name: 'Saskatchewan', code: 'SK' },
    { name: 'Yukon Territory', code: 'YT' },
  ],
}

const GBR: CountryInfo = {
  countryCode: locales['en-GB']?.countryCode,
  countryIsoCode: locales['en-GB']?.countryIsoCode,
  currency: locales['en-GB']?.currency,
  addressDataset: locales['en-GB']?.addressDataset,
  labels: {
    region: 'County',
  },
  regions: [],
}

export const countries: Record<CountryCode | CountryIsoCode, CountryInfo> = {
  USA,
  CAN,
  GBR,
  US: USA,
  CA: CAN,
  GB: GBR,
}

export const countryCodeISOMap = {
  US: 'USA',
  CA: 'CAN',
  GB: 'GBR',
} as const
